/**
 * Profile Service
 * Handles profile-related API operations
 */
import { apiClient } from '@/lib/api/client';
import {
  MeResponse,
  MyOrganizationMembership,
  Profile,
  UpdateOwnProfileInput,
} from '@sera-teams/shared';

interface UploadProfilePhotoResponse {
  photoUrl: string;
}

class ProfileService {
  private readonly basePath = '/api/v1/profile';

  /**
   * Upload a new profile photo
   * Uses: POST /api/v1/profile/photo
   * @param file - File object from input element
   * @returns The uploaded photo URL
   */
  async uploadPhoto(file: File): Promise<string> {
    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Post method will detect FormData and handle it properly
    // apiClient will not add Content-Type header for FormData (browser sets it with boundary)
    const response = await apiClient.post<UploadProfilePhotoResponse>(
      `${this.basePath}/photo`,
      formData
    );

    if (!response?.photoUrl) {
      throw new Error('Failed to upload photo: Invalid response');
    }

    return response.photoUrl;
  }

  async getMe(organizationId?: string): Promise<MeResponse> {
    return apiClient.get<MeResponse>(
      '/api/v1/me',
      organizationId ? { organization_id: organizationId } : undefined
    );
  }

  async getMyOrganizations(): Promise<MyOrganizationMembership[]> {
    return apiClient.get<MyOrganizationMembership[]>(
      '/api/v1/me/organizations'
    );
  }

  async updateProfile(input: UpdateOwnProfileInput): Promise<Profile> {
    return apiClient.patch<Profile>(this.basePath, input);
  }

  /** Create an org the caller owns; every onboarding field must be present */
  async createOwnOrganization(
    input: Record<string, unknown>
  ): Promise<{ organization_id: string }> {
    return apiClient.post<{ organization_id: string }>(
      '/api/v1/me/organizations',
      input
    );
  }

  async getMetadata(): Promise<Record<string, unknown>> {
    const response = await apiClient.get<Record<string, unknown>>(
      `${this.basePath}/metadata`
    );

    if (!response) {
      throw new Error('Failed to fetch profile metadata');
    }

    return response;
  }

  async updateMetadata(
    patch: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.patch<Record<string, unknown>>(
      `${this.basePath}/metadata`,
      patch
    );

    if (!response) {
      throw new Error('Failed to update profile metadata');
    }

    return response;
  }
}

// Export singleton instance
export const profileService = new ProfileService();
export default profileService;
